<script setup lang="ts">
import { cancellationBlocks, type Cancellation, type Modification } from '../order-management';
withDefaults(
  defineProps<{
    offers: Array<{
      offerId: string;
      displayName: string;
      photoDataUrl?: string;
      amountMinor?: number;
      currency?: string;
      products?: unknown[];
    }>;
    statusesAvailable?: boolean;
    showPrices?: boolean;
    orders?: Array<{
      selectedBoxOfferId?: string;
      deliveryLifecycleState?: string;
      cancellation?: Cancellation;
      modifications?: Modification[];
    }>;
  }>(),
  { statusesAvailable: true, showPrices: true },
);
defineEmits<{ select: [offerId: string] }>();
const money = (value: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value / 100);
function status(
  offer: { offerId: string; products?: unknown[] },
  orders: Array<{
    selectedBoxOfferId?: string;
    deliveryLifecycleState?: string;
    cancellation?: Cancellation;
    modifications?: Modification[];
  }> = [],
  available = true,
) {
  if (!available) return { label: 'État des commandes indisponible', tone: 'unknown' };
  const linked = orders.filter((o) => o.selectedBoxOfferId === offer.offerId);
  const urgent = linked.filter(
    (o) =>
      (cancellationBlocks(o) && o.deliveryLifecycleState !== 'CANCELLED') ||
      ['REFUSEE', 'MODIFICATION_PROPOSEE'].includes(o.deliveryLifecycleState ?? ''),
  ).length;
  if (urgent)
    return { label: `${urgent} commande${urgent > 1 ? 's' : ''} à traiter`, tone: 'urgent' };
  const ongoing = linked.filter((o) =>
    ['RECEIVED', 'PREPARING', 'READY'].includes(o.deliveryLifecycleState ?? ''),
  ).length;
  if (ongoing)
    return { label: `${ongoing} commande${ongoing > 1 ? 's' : ''} en cours`, tone: 'progress' };
  if (!offer.products?.length) return { label: 'Composition à renseigner', tone: 'urgent' };
  return { label: 'Au catalogue', tone: 'done' };
}
</script>
<template>
  <div class="offer-gallery" aria-label="Vignettes des offres">
    <button
      v-for="offer in offers"
      :key="offer.offerId"
      class="offer-tile"
      type="button"
      :data-testid="`offer-tile-${offer.offerId}`"
      @click="$emit('select', offer.offerId)"
    >
      <img v-if="offer.photoDataUrl" :src="offer.photoDataUrl" :alt="offer.displayName" />
      <div v-else class="placeholder" aria-hidden="true">
        <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="2">
          <path d="m8 14 16-7 16 7v21l-16 7-16-7zM8 14l16 7 16-7M24 21v21M16 10l16 7" />
        </svg>
      </div>
      <div class="caption">
        <small>Pack du catalogue</small>
        <strong>{{ offer.displayName }}</strong
        ><span v-if="showPrices && offer.amountMinor !== undefined">{{
          money(offer.amountMinor)
        }}</span
        ><span class="status" :class="status(offer, orders, statusesAvailable).tone">{{
          status(offer, orders, statusesAvailable).label
        }}</span
        ><small>Voir les détails →</small>
      </div>
    </button>
  </div>
</template>
<style scoped>
.offer-gallery {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.7rem;
  width: 100%;
}
.offer-tile {
  min-width: 0;
  padding: 0;
  border: 1px solid #cbd5e1;
  border-radius: 1rem;
  overflow: hidden;
  text-align: left;
  background: white;
  color: #172033;
  box-shadow: 0 3px 8px #17203315;
  cursor: pointer;
}
.offer-tile img,
.placeholder {
  width: 100%;
  aspect-ratio: 4/3;
  object-fit: cover;
  display: block;
}
.placeholder {
  display: grid;
  place-items: center;
  background: #e8eef7;
  color: #20214f;
}
svg {
  width: 50%;
  max-height: 90px;
}
.caption {
  display: grid;
  gap: 0.6rem;
  padding: 0.8rem;
  overflow-wrap: anywhere;
}
.status {
  font-size: 0.8rem;
  font-weight: 800;
  border-radius: 0.5rem;
  padding: 0.4rem 0.5rem;
}
.urgent {
  background: #b91c1c;
  color: white;
}
.progress {
  background: #ffedd5;
  color: #9a3412;
  border: 1px solid #f97316;
}
.done {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #22c55e;
}
small {
  font-weight: 700;
}
</style>

<style scoped>
.unknown {
  background: #e2e8f0;
  color: #334155;
}
</style>
